import {
	Component, OnInit, AfterViewInit, ViewChild, OnDestroy, Input, EventEmitter, Output, ViewChildren, QueryList,
	ElementRef, HostListener, Optional
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IOrganisationsHierarchy, OrganisationDataService } from '@services/organisation-data.service';
import { LevelAsset, AssetsDataService, WatchedAssetData } from '@services/assets-data.service'

import { animate, state, style, transition, trigger } from '@angular/animations';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { GlobalFunctions, SnackType } from '@global/GlobalFunctions';
import { distinctUntilChanged, filter, finalize, take, takeUntil } from 'rxjs/operators';
import { combineLatest, firstValueFrom, Subject } from 'rxjs'
import { Account, IAccount } from '@services/accounts.service';
import { DeviationEvent, EventComment, EventsDataService, OfflineEvent, RuleEvent } from '@services/events-data.service';
import { convertEpochToDateString } from '@functions/functions';
import { RulesDataService } from '@services/rules-data.service';
import { DatapointsDataService } from '@services/datapoints-data.service';
import { MatCheckbox } from '@angular/material/checkbox';
import { AuthenticationService, WebAppUser } from '@services/authentication.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CommentDialogComponent } from '@dialogs/comment-dialog/comment-dialog.component';
import { CreateActionComponent, createActionDialogData } from '@dialogs/create-action/create-action.component';
import { Action, ActionsDataService } from '@services/actions-data.service';

/**
 * Default Class
 */
@Component({
	selector: 'app-watch-list',
	templateUrl: './watch-list.component.html',
	styleUrls: ['./watch-list.component.scss'],
	animations: [
		trigger('detailExpand', [
			state('collapsed', style({ height: '0px', minHeight: '0' })),
			state('expanded', style({ height: '*' })),
			transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
		]),
	]
})
export class WatchListComponent implements OnInit, OnDestroy, AfterViewInit {

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** Paginator */
	@ViewChild(MatPaginator) paginator!: MatPaginator;

	@ViewChild('infodiv') infodiv!: ElementRef;

	@ViewChildren('ruleCheckboxes') ruleCheckboxes!: QueryList<MatCheckbox>;
	@ViewChildren('sensorCheckboxes') sensorCheckboxes!: QueryList<MatCheckbox>;
	@ViewChildren('deviationCheckboxes') deviationCheckboxes!: QueryList<MatCheckbox>;

	/** Watch Status */
	public watchStatus: boolean = true
	/** Selected Nodes */
	public selectedNodes: string = ""
	/** Selected Node Name */
	public selectedNodeName: string = ""
	/** Assets */
	public assets: LevelAsset[] = [];
	/** Asset */
	public asset: LevelAsset;
	/** Datasource */
	public dataSource!: MatTableDataSource<any>;
	/** Columns to display */
	public columnsToDisplay = ['assetID', 'name', 'description', 'watch']
	/** Extended columns to display */
	columnsToDisplayWithExpand = [...this.columnsToDisplay, 'expand'];
	/** Element Extended */
	public expandedElement!: LevelAsset | null;

	/** accountID */
	@Input() accountID: string;
	/** selectedAccount */
	public selectedAccount: IAccount;
	/** OrganisationID */
	@Input() public OrganisationUID: string;

	/** ruleHistory */
	public ruleHistory: RuleEvent[] = [];
	/** offlineSensors */
	public offlineSensors: OfflineEvent[] = [];
	/** offlineSensors */
	public DeviationEvents: DeviationEvent[] = [];

	/** watchedData containing which rule and offline events are 
	 * applicable to this asset
	 */
	public watchedData: WatchedAssetData;
	/** ruleEventIDs from DB */
	public ruleEventIDs: number[] = [];
	/** offlineEventIDs from DB */
	public offlineEventIDs: number[] = [];

	public timeStamp: number = 0;

	@Output() listChanged = new EventEmitter<boolean>();

	/** hierarchicalData */
	public hierarchicalData: IOrganisationsHierarchy[];

	public fieldsRule: Object = { text: 'RuleName', id: 'RuleEventID' };
	public fieldsSensor: Object = { text: 'SensorName', id: 'OfflineEventID' };
	public fieldsDeviation: Object = { text: 'AssetName', id: 'DeviationEventID' };

	public onWatchList: boolean = true;

	public ruleEventsToAcknowledge: RuleEvent[] = [];
	public sensorEventsToAcknowledge: OfflineEvent[] = [];
	public deviationEventsToAcknowledge: DeviationEvent[] = [];

	isMobileView: boolean = false;

	resizeObserver!: ResizeObserver;

	public assetID: number;

	public RelatedComments: EventComment[] = [];

	public hierarchy: IOrganisationsHierarchy;

	public containsRuleEvents: boolean = false;

	public containsSensorEvents: boolean = false;

	public containsDeviationEvents: boolean = false;

	public numberOfTypes: number = 0;

	public currentUser: WebAppUser;

	/**
	 * Default Constructor
	 * @param route route
	 * @param organisationDataService OrganisationData
	 * @param assetsDataService AssetsDataService
	 * @param siteSubscriptions siteSubscriptions
	 * @param globalFunctions globalFunctions
	 */
	constructor(
		private route: ActivatedRoute,
		private router: Router,
		public organisationDataService: OrganisationDataService,
		public assetsDataService: AssetsDataService,
		public rulesDataService: RulesDataService,
		public eventsDataService: EventsDataService,
		public datapointsDataService: DatapointsDataService,
		public actionsDataService: ActionsDataService,
		private siteSubscriptions: siteSubscriptions,
		private globalFunctions: GlobalFunctions,
		public dialog: MatDialog,
		private authenticationService: AuthenticationService,
		@Optional() public dialogRef?: MatDialogRef<WatchListComponent>
	) {
		combineLatest([
			this.siteSubscriptions.selectedAsset$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.authenticationService.currentUser$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.route.paramMap.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.route.queryParamMap.pipe(takeUntil(this.finalise), distinctUntilChanged())
		]).pipe(
			// Ensure that subscriptions are not null or undefined
			filter(([asset, currentUser, paramMap, queryParamMap]) =>
				!!asset && !!currentUser && !!paramMap && !!queryParamMap)
		).subscribe({
			next: async ([asset, currentUser, paramMap, queryParamMap]) => {
				this.globalFunctions.displaySpinner(true);

				let [selectedAccount, selectedAccountGUID, selectedOrganisation, selectedOrganisationUID] = await firstValueFrom(
					combineLatest([
						this.siteSubscriptions.selectedAccount$.pipe(take(1)),
						this.siteSubscriptions.selectedAccountGUID$.pipe(take(1)),
						this.siteSubscriptions.selectedOrganisation$.pipe(take(1)),
						this.siteSubscriptions.selectedOrganisationUID$.pipe(take(1))
					]
					)
				);

				this.selectedAccount = selectedAccount;
				this.selectedAccount = this.siteSubscriptions.normalizeFeatures(this.selectedAccount) as Account;

				this.accountID = selectedAccountGUID;
				this.OrganisationUID = selectedOrganisationUID;

				this.currentUser = currentUser;

				let paramAsset = paramMap.get('assetID');

				if (!paramAsset) {
					let splitURL: string[] = this.router.url.split("/");
					let urlAsset = splitURL[splitURL.length - 1];
					if (urlAsset) {
						paramAsset = urlAsset;
					}
				}

				const currentURL = this.router.url;
				if (currentURL.includes('asset')) {
					this.onWatchList = false;
				}

				if (typeof (asset) == 'string') {
					asset = JSON.parse(asset)
					this.asset = asset
				} else {
					this.asset = asset
				}

				if (paramAsset) {
					let pAssetID = parseInt(paramAsset)
					if (pAssetID != this.assetID) {
						this.assetID = pAssetID;
						this.getWatchedData(asset.AssetID);
					} else {
						this.globalFunctions.displaySpinner(false);
					}
				}

				this.checkScreenSize();
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
			}
		});
	}

	/** setupResizeObserver */
	setupResizeObserver() {
		// Create a new ResizeObserver to listen for size changes of the parent div
		this.resizeObserver = new ResizeObserver((entries) => {
			for (let entry of entries) {
				const parentDivWidth = entry.contentRect.width;  // Get the parent's width
				const windowWidth = window.innerWidth;

				// Set isMobileView based on window width or parent div size
				this.isMobileView = windowWidth < 800 || parentDivWidth < 800;
			}
		});

		// Observe the infoDiv element
		if (this.infodiv) {
			this.resizeObserver.observe(this.infodiv.nativeElement);
		}
	}

	/** goToFunctionalLocation */
	goToFunctionalLocation(assetID: number) {
		this.router.navigate([`/${this.accountID}/organisation/${this.OrganisationUID}/location/${this.asset.ParentUID}/asset/${assetID}`]);
	}

	/** getWatchedData */
	getWatchedData(assetID: number) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.watchedData = new WatchedAssetData(0, false);
		this.ruleEventIDs = [];
		this.ruleHistory = [];
		this.offlineEventIDs = [];
		this.offlineSensors = [];
		this.timeStamp = 0;
		this.RelatedComments = [];
		this.containsRuleEvents = false;
		this.containsSensorEvents = false;
		this.numberOfTypes = 0;

		this.assetsDataService.getWatchedAssetData$(this.accountID, this.OrganisationUID, assetID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
				this.globalFunctions.displaySpinner(false);
			})
		).subscribe({
			next: (data: any) => {
				this.watchedData = data[0];
				if (this.watchedData.TimeStamp) {
					this.timeStamp = this.watchedData.TimeStamp
				}
				if (this.watchedData.TriggeredRules) {
					this.ruleHistory = JSON.parse(this.watchedData.TriggeredRules);
					this.containsRuleEvents = true;
					this.numberOfTypes++;
				}
				if (this.watchedData.TriggeredDataPoints) {
					this.offlineSensors = JSON.parse(this.watchedData.TriggeredDataPoints);
					this.containsSensorEvents = true;
					this.numberOfTypes++;
				}
				if (this.watchedData.TriggeredDeviations) {
					this.DeviationEvents = JSON.parse(this.watchedData.TriggeredDeviations);
					this.containsDeviationEvents = true;
					this.numberOfTypes++;
				}
				if (this.watchedData.RelatedComments) {
					this.RelatedComments = JSON.parse(this.watchedData.RelatedComments);
				}
			},
			error: (error) => {
				if (error.status != 404 && error.status != 500) {
					this.globalFunctions.HandleError(error, "WatchList Error: this.assetsDataService.getWatchedAssetData$", false)
				}
			},
		});
	}

	// Detect screen size change
	@HostListener('window:resize', ['$event'])
	onResize(event: any) {
		this.checkScreenSize();
	}

	/** checkScreenSize */
	checkScreenSize() {
		this.isMobileView = window.innerWidth < 800;
		// this.isMobileView = this.infodiv.nativeElement.offsetWidth < 800;
	}

	/** acknowledge */
	acknowledge(addComment: boolean, deviations?: boolean) {
		if (addComment) {
			if (!deviations) {
				this.addComment(this.ruleEventsToAcknowledge, 2);
				return;
			} else {
				this.addComment(this.deviationEventsToAcknowledge, 4);
				return;
			}
		}

		let tempWatchData: WatchedAssetData = { ...this.watchedData };

		let newRuleEvents: RuleEvent[] = this.ruleHistory.filter((ruleEvent: RuleEvent) => !this.ruleEventsToAcknowledge.includes(ruleEvent));
		let arr: number[] = [];
		arr = newRuleEvents.map((x: RuleEvent) => x.RuleEventID);
		tempWatchData.TriggeredRules = arr.toString();

		let newSensorEvents: OfflineEvent[] = this.offlineSensors.filter((sensorEvent: OfflineEvent) => !this.sensorEventsToAcknowledge.includes(sensorEvent));
		let arr2: number[] = [];
		arr2 = newSensorEvents.map((x: OfflineEvent) => x.OfflineEventID);
		tempWatchData.TriggeredDataPoints = arr2.toString();

		let newDeviationEvents: DeviationEvent[] = this.DeviationEvents.filter((deviationEvent: DeviationEvent) => !this.deviationEventsToAcknowledge.includes(deviationEvent))
		let arr3: number[] = [];
		arr3 = newDeviationEvents.map((x: DeviationEvent) => x.DeviationEventID);
		tempWatchData.TriggeredDeviations = arr3.toString();

		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();
		if (this.watchedData.WatchListAssetID) {
			this.assetsDataService.patchWatchedAssetData$(this.accountID, this.OrganisationUID, this.watchedData.WatchListAssetID, tempWatchData).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
					this.ruleHistory = newRuleEvents;
					this.ruleEventIDs = arr;
					this.offlineSensors = newSensorEvents;
					this.offlineEventIDs = arr2;
					if (this.ruleEventsToAcknowledge.length > 0) {
						this.createAction(this.ruleEventsToAcknowledge[0]);
					}
					this.ruleEventsToAcknowledge = [];
					this.sensorEventsToAcknowledge = [];
					this.globalFunctions.DisplaySnackBar({
						message: "Acknowledged",
						duration: 2000,
						type: SnackType.Info,
						action: ""
					})
					this.siteSubscriptions.RefreshWatchList(true);
					this.subscribeOrganisationStructure(this.accountID, this.OrganisationUID);
					this.getWatchedData(this.asset.AssetID);
				})
			).subscribe({
				next: (watchedData: any) => {
					// console.log(watchedData)
				},
				error: (error) => {
					if (error.status != 404) {
						this.globalFunctions.HandleError(error, "WatchList Error: this.assetsDataService.patchWatchedAssetData$", false)
					}
				},
			});
		}
	}

	/** addRuleEventToAcknowledge */
	addRuleEventToAcknowledge(ruleEvent: RuleEvent, event: any) {
		if (event.checked) {
			this.ruleEventsToAcknowledge.push(ruleEvent);
		} else {
			this.ruleEventsToAcknowledge = this.ruleEventsToAcknowledge.filter((x: RuleEvent) => x.RuleEventID != ruleEvent.RuleEventID)
		}
	}

	/** addAllRuleEvents */
	addAllRuleEvents() {
		this.ruleEventsToAcknowledge = this.ruleHistory;
		this.ruleCheckboxes.forEach((checkbox) => {
			checkbox.checked = true;
		});
	}

	/** unAddAllRuleEvents */
	unAddAllRuleEvents() {
		this.ruleEventsToAcknowledge = [];
		this.ruleCheckboxes.forEach((checkbox) => {
			checkbox.checked = false;
		});
	}

	/** addSensorEventToAcknowledge */
	addSensorEventToAcknowledge(sensorEvent: OfflineEvent, event: any) {
		if (event.checked) {
			this.sensorEventsToAcknowledge.push(sensorEvent);
		} else {
			this.sensorEventsToAcknowledge = this.sensorEventsToAcknowledge.filter((x: OfflineEvent) => x.OfflineEventID != sensorEvent.OfflineEventID);
		}
	}

	/** addAllSensorEvents */
	addAllSensorEvents() {
		this.sensorEventsToAcknowledge = this.offlineSensors;
		this.sensorCheckboxes.forEach((checkbox) => {
			checkbox.checked = true;
		});
	}

	/** unAddAllSensorEvents */
	unAddAllSensorEvents() {
		this.sensorEventsToAcknowledge = [];
		this.sensorCheckboxes.forEach((checkbox) => {
			checkbox.checked = false;
		});
	}

	/** addSensorEventToAcknowledge */
	addDeviationEventToAcknowledge(deviationEvent: DeviationEvent, event: any) {
		if (event.checked) {
			this.deviationEventsToAcknowledge.push(deviationEvent);
		} else {
			this.deviationEventsToAcknowledge = this.deviationEventsToAcknowledge.filter((x: DeviationEvent) => x.DeviationEventID != deviationEvent.DeviationEventID);
		}
	}

	/** addAllSensorEvents */
	addAllDeviationEvents() {
		this.deviationEventsToAcknowledge = this.DeviationEvents;
		this.deviationCheckboxes.forEach((checkbox) => {
			checkbox.checked = true;
		});
	}

	/** unAddAllSensorEvents */
	unAddAllDeviationEvents() {
		this.deviationEventsToAcknowledge = [];
		this.deviationCheckboxes.forEach((checkbox) => {
			checkbox.checked = false;
		});
	}

	/** emitChange */
	emitChange() {
		this.listChanged.emit(true);
	}

	/** goToRuleEvent */
	goToRuleEvent(ruleEvent: RuleEvent) {
		if (this.dialogRef) {
			this.dialogRef.close();
		}
		this.router.navigate([`./${this.accountID}/organisation/${this.OrganisationUID}/event/${ruleEvent.RuleEventID}`], { queryParams: { eventType: 2 } })
	}

	/** goToOfflineEvent */
	goToOfflineEvent(offlineEvent: OfflineEvent) {
		if (this.dialogRef) {
			this.dialogRef.close();
		}
		this.router.navigate([`./${this.accountID}/organisation/${this.OrganisationUID}/event/${offlineEvent.OfflineEventID}`], { queryParams: { eventType: 1 } })
	}

	/** goToOfflineEvent */
	goToDeviationEvent(deviationEvent: DeviationEvent) {
		if (this.dialogRef) {
			this.dialogRef.close();
		}
		this.router.navigate([`./${this.accountID}/organisation/${this.OrganisationUID}/event/${deviationEvent.DeviationEventID}`], { queryParams: { eventType: 4 } })
	}

	/** goToCommentEvent */
	goToCommentEvent(comment: EventComment) {
		if (this.dialogRef) {
			this.dialogRef.close();
		}
		if (comment.EventType == 2) {
			this.router.navigate([`./${this.accountID}/organisation/${this.OrganisationUID}/event/${comment.EventID}`], { queryParams: { eventType: 2 } })
		} else if (comment.EventType == 1) {
			this.router.navigate([`./${this.accountID}/organisation/${this.OrganisationUID}/event/${comment.EventID}`], { queryParams: { eventType: 1 } })
		}
	}

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit(): void {

		// this.route.queryParams.pipe(
		// 	takeUntil(this.finalise), distinctUntilChanged()
		// ).subscribe(params => {
		// 	// this.watchStatus = params['watch']|| false
		// 	this.route.paramMap.pipe(
		// 		takeUntil(this.finalise), distinctUntilChanged()
		// 	).subscribe(
		// 		params => {
		// 			const paramAsset = params.get('assetID');
		// 			if (paramAsset) {
		// 				this.selectedNodes = paramAsset

		// 				// let Found: any
		// 				// this.siteSubscriptions.hierarchicalData$.pipe(
		// 				// 	takeUntil(this.finalise), distinctUntilChanged()
		// 				// ).subscribe({
		// 				// 	next: (data: string) => {
		// 				// 		try {
		// 				// 			let orgData = <IOrganisationsHierarchy[]>JSON.parse(data)
		// 				// 			Found = this.organisationDataService.findObjectByUID(paramAsset, orgData)
		// 				// 			if (Found) {
		// 				// 				// console.log(Found)
		// 				// 				this.selectedNodeName = Found.description

		// 				// 				// const result: any = this.OrganisationData.findSubChildNamesById(Found, paramAsset)
		// 				// 				this.assets = <LevelAsset[]>this.assetsDataService.findAssets(Found, this.watchStatus)// <AssetItem[]>result

		// 				// 				// this.assets = Found.assets || ""
		// 				// 			} else {
		// 				// 				this.selectedNodeName = ""
		// 				// 				this.assets = []
		// 				// 			}
		// 				// 		}
		// 				// 		catch (error) { console.error(error) }

		// 				// 		this.dataSource = new MatTableDataSource(this.assets);
		// 				// 	},
		// 				// 	error: (error) => { this.globalFunctions.HandleError(error, "Error: this.siteSubscriptions.hierarchicalData$", false) },
		// 				// 	//complete: () => { console.log("this.siteSubscriptions.hierarchicalData$.subscribe: Complete"); }
		// 				// });

		// 			}
		// 			else {
		// 				this.selectedNodes = "";
		// 				this.selectedNodeName = ""
		// 			}
		// 		}
		// 	)

		// });
		// this.route.queryParams.subscribe(params => {

		//     this.queryStatus = params['status']

		// })
	}

	/**
	 * convertEpoch
	 * @param epoch
	 * @returns
	 */
	convertEpoch(epoch: number): string {
		/** Use the Global Function */
		return convertEpochToDateString(epoch);
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}

	/**
	 * Page after view init Event Handler
	 */
	ngAfterViewInit() {
		// this.paginator.pageSize = 25
		// this.dataSource.paginator = this.paginator;
		this.setupResizeObserver();
	}

	/**
	* Get the Organisation Structure
	* @param AccountID AccountID
	* @param OrganisationID OrganisationID
	*/
	subscribeOrganisationStructure(AccountID: string, OrganisationUID: string, ShowHidden: number = 0) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		try {
			/** Get the Organisation for the selection */
			this.organisationDataService.getOrganisationStructure$(AccountID, OrganisationUID, ShowHidden).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((structure) => !!structure),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (structure: any) => {
					try {
						/** Update the Organisation */
						var OrganisationStructure = <IOrganisationsHierarchy[]>JSON.parse(structure);
						this.siteSubscriptions.SethierarchicalData(OrganisationStructure);
					} catch (error) { console.error(error); }
				},
				error: (error) => {
					if (error["status"] != 404) {
						this.globalFunctions.HandleError(error, "Error: OrganisationData.getOrganisationStructure$(OrganisationUID).subscribe$", false)
					}
				},
				//complete: () => { console.log("OrganisationData.getOrganisationStructure$(OrganisationUID).subscribe: Complete"); }
			});
		} catch (error) { console.error(error); }
	}

	/** addCommentNew */
	addComment(events: any[], eventType: number) {
		const dialogRef = this.dialog.open(CommentDialogComponent, {
			width: '500px',
			data: {
				"HeaderText": "Add Comment",
				"DialogText": "This comment will be added to all Events that are being acknowledged"
			}
		});
		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
			let _comment = result.comment;

			events.forEach(event => {
				const fetchDataSubject = new Subject<void>();

				let comment: EventComment = {
					CommentText: _comment,
					EventID: eventType == 2 ? event.RuleEventID : eventType == 4 ? event.DeviationEventID : 0,
					EventType: eventType
				}

				this.eventsDataService.postEventComment$(this.accountID, this.OrganisationUID, comment.EventID, comment, eventType, this.currentUser.UserID).pipe(
					takeUntil(fetchDataSubject), distinctUntilChanged(),
					filter((response) => !!response),
					finalize(() => {
						// Ensure the subscription is terminated and the subject is completed
						fetchDataSubject.next();
						fetchDataSubject.complete();
					})
				).subscribe({
					next: (response) => {
					}
				});
			});

			this.acknowledge(false);
		});
	}

	/**
	 * createAction
	 */
	createAction(event: RuleEvent) {
		let dialogData = <createActionDialogData>{
			assetItem: this.asset,
			isAttachedtoEvent: true
		}

		const dialogRef = this.dialog.open(CreateActionComponent, {
			data: dialogData
		});
		dialogRef.afterClosed().pipe(
		).subscribe(result => {
			if (!result) {
				this.createAction(event);
			} else {
				if (!result.skipAction) {
					let newAction: Action = {
						ActionID: 0,
						ActionType: result.actionType,
						AssetID: this.asset.AssetID,
						AssetName: `${this.asset.Name} - ${this.asset.Description}`,
						EventID: event.RuleEventID,
						TimeLineID: event.TimelineID,
						Timestamp: 0,
						Completed: result.isHistoric,
						Details: result.details,
						CompletedTimestamp: result.isHistoric ? result.targetCompletionTimestamp.getTime() : 0,
						CompletedByUserID: result.isHistoric ? result.assignedToUser : '',
						CreatedByUserSub: '',
						AssignedToUserSub: result.assignedToUser,
						WorkOrderReference: result.workOrderReference,
						TargetCompletionTimestamp: result.targetCompletionTimestamp.getTime(),
						DisabledRules: result.ruleList ? result.ruleList.toString() : ''
					}
					let disable;
					if (result.disableRule) {
						disable = true;
					}

					const fetchDataSubject = new Subject<void>();

					this.actionsDataService.postAction$(this.accountID, this.OrganisationUID, newAction, disable).pipe(
						takeUntil(fetchDataSubject), distinctUntilChanged(),
						filter((action) => !!action),
						finalize(() => {
							// Ensure the subscription is terminated and the subject is completed
							fetchDataSubject.next();
							fetchDataSubject.complete();
						})
					).subscribe({
						next: (action) => {
							this.globalFunctions.DisplaySnackBar({
								message: "Action Created",
								type: SnackType.Info,
								duration: 1000
							});
						}
					});
				}
			}
		});
	}

	/**
	 * Cancel button for dialog
	 */
	onCancelClick(): void {
		if (this.dialogRef) {
			this.dialogRef.close();
		}
	}
}
